import React from 'react';
import { useTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import type { TrialPhase, TrialType } from '../../types';
import { TrialPopover } from '../../common/trialPopover';
import { TrialBaseBadge } from '../../common/trialBaseBadge';

interface FeatureGateBadgeProps {
  isTrialActive: boolean;
  daysRemaining?: number;
  trialPhase?: TrialPhase;
  trialType?: TrialType;
  featureLabel?: string;
  priceLabel?: string;
  description?: string;
  onUpgrade?: () => void;
  compact?: boolean;
}

const getBadgeLabel = (isTrialActive: boolean, trialType?: TrialType): string => {
  if (isTrialActive) {
    return trialType === 'addon' ? 'Add-on Trial' : 'Trial Feature';
  }
  return trialType === 'addon' ? 'Add-on' : 'Growth Feature';
};

const getTrialActiveDescription = (trialType?: TrialType): string => {
  const ctaText = trialType === 'addon' ? 'Purchase the add-on' : 'Upgrade';
  return `This feature is available during your trial. ${ctaText} to keep access after your trial ends.`;
};

const getLockedDescription = (
  featureLabel: string,
  trialType?: TrialType,
  priceLabel?: string,
): string => {
  const action =
    trialType === 'addon' ? `Purchase the ${featureLabel} add-on` : `Upgrade to ${featureLabel}`;
  return `${action} to unlock this feature${priceLabel ? ` \u2014 ${priceLabel}` : ''}.`;
};

export const FeatureGateBadge: React.FC<FeatureGateBadgeProps> = ({
  isTrialActive,
  daysRemaining,
  trialPhase,
  trialType,
  featureLabel = 'Growth',
  priceLabel,
  description,
  onUpgrade,
  compact = false,
}) => {
  const { token } = useTheme();
  const badgeLabel = getBadgeLabel(isTrialActive, trialType);

  if (compact) {
    return <TrialBaseBadge label="Trial feature" trialPhase={trialPhase} compact />;
  }

  if (isTrialActive) {
    return (
      <TrialPopover
        title={badgeLabel}
        description={description ?? getTrialActiveDescription(trialType)}
        daysRemaining={daysRemaining}
        trialType={trialType}
        onAction={onUpgrade}
      >
        <TrialBaseBadge label={badgeLabel} trialPhase={trialPhase} />
      </TrialPopover>
    );
  }

  return (
    <TrialPopover
      title={badgeLabel}
      description={getLockedDescription(featureLabel, trialType, priceLabel)}
      trialType={trialType}
      onAction={onUpgrade}
    >
      <TrialBaseBadge
        label={badgeLabel}
        bgColor={token['yellow.4']}
        icon={<FontAwesomeIcon icon={faLock} size="xs" />}
      />
    </TrialPopover>
  );
};
